import { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL, POLL_INTERVAL_MS, POLL_TIMEOUT_MS } from '../constants';
import { applyReportStyling } from '../utils/reportStyling';

export function useFileUpload() {
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('');
  const [status, setStatus] = useState('initial');
  // null | 'timeout' | 'network' — tailors the error message shown on 'fail'.
  const [failReason, setFailReason] = useState(null);
  const [textInput, setTextInput] = useState('');
  const [textUploaded, setTextUploaded] = useState(false);

  const [report, setReport] = useState(null);
  const [styledReport, setStyledReport] = useState(null);
  const [disclaimer, setDisclaimer] = useState(null);
  const [containsNDA, setContainsNDA] = useState(false);
  const [ndaReport, setNdaReport] = useState(null);

  // Race safety: each polling run gets a monotonically increasing id. A run only
  // applies its fetch result / schedules its next tick if its id is still the
  // current one. Bumping the id (via stopPolling) instantly invalidates any
  // in-flight fetch so a late response can't clobber a newer attempt or a reset.
  const pollRunIdRef = useRef(0);
  const pollTimerRef = useRef(null);
  const timeoutTimerRef = useRef(null);

  // Stop any active polling loop and clear its timers. Bumps the run id so an
  // in-flight fetch from the old run becomes a no-op when it resolves.
  const stopPolling = useCallback(() => {
    pollRunIdRef.current += 1;
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (timeoutTimerRef.current) {
      clearTimeout(timeoutTimerRef.current);
      timeoutTimerRef.current = null;
    }
  }, []);

  const handleFileChange = useCallback((e) => {
    stopPolling();
    setFailReason(null);
    if (e.target.files && e.target.files.length > 0) {
      const uploadedFile = e.target.files[0];
      setStatus('initial');
      setFile(uploadedFile);
      const fileExtension = uploadedFile.name.split('.').pop();
      setFileType(fileExtension);
      setTextUploaded(false);
    } else {
      setFile(null);
      setFileType('');
    }
  }, [stopPolling]);

  const handleTextChange = useCallback((text) => {
    setTextInput(text);
    if (textUploaded) {
      setTextUploaded(false);
    }
  }, [textUploaded]);

  const handleTextSubmit = useCallback(() => {
    if (textInput) {
      stopPolling();
      setFailReason(null);
      const blob = new Blob([textInput], { type: 'text/plain' });
      const fileName = `textinput_${Math.random().toString(36).substring(7)}`;
      const textFile = new File([blob], `${fileName}.txt`, { type: 'text/plain' });
      setFile(textFile);
      setFileType('txt');
      setStatus('initial');
      setTextUploaded(true);
    }
  }, [textInput, stopPolling]);

  const startPolling = useCallback((id) => {
    // Begin a fresh run; invalidate any prior run and clear its timers.
    stopPolling();
    const runId = pollRunIdRef.current;

    // Hard timeout: stop polling and surface a recoverable error.
    timeoutTimerRef.current = setTimeout(() => {
      if (runId !== pollRunIdRef.current) return;
      stopPolling();
      setFailReason('timeout');
      setStatus('fail');
    }, POLL_TIMEOUT_MS);

    const poll = async () => {
      // Bail if this run was superseded (retry/reset/unmount/timeout).
      if (runId !== pollRunIdRef.current) return;
      try {
        const response = await fetch(`${API_URL}/getReport?id=${id}`);
        const data = await response.json();

        // The run may have been cancelled while the fetch was in flight.
        if (runId !== pollRunIdRef.current) return;

        if (data.status === 'completed') {
          stopPolling();
          setStatus('success');
          setFailReason(null);

          let reportContent = data.report;
          if (data.report) {
            reportContent = reportContent.toString();
          } else {
            reportContent = 'No report available. Please try again.';
          }

          if (data.nda_template) {
            setNdaReport(data.nda_template);
          } else {
            setNdaReport(null);
          }

          setContainsNDA(data.contains_NDA);
          setReport(reportContent);
          setDisclaimer(data.disclaimer);
          setStyledReport(applyReportStyling(data.report));
          setFile(null);
          setTextUploaded(false);
          setTextInput('');
          return;
        }

        // Not done yet — schedule the next tick (recursive setTimeout avoids
        // overlapping async polls that a setInterval would allow).
        pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (error) {
        console.error(error);
        if (runId !== pollRunIdRef.current) return;
        stopPolling();
        setFailReason('network');
        setStatus('fail');
        setReport('No report available. Please try again.');
        setStyledReport('No report available. Please try again.');
      }
    };

    // First poll after one interval (matches the original setInterval timing;
    // the backend takes minutes, so an immediate poll buys nothing).
    pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
  }, [stopPolling]);

  const handleUpload = useCallback(async (selectedState) => {
    if (!file) {
      return null;
    }

    stopPolling();
    setFailReason(null);
    setStatus('uploading');
    let state = 'NA';
    if (selectedState.value !== 'N/A') {
      state = selectedState.value;
    }
    const url = `${API_URL}/upload/${fileType}/${state}`;

    try {
      const response = await fetch(url, {
        method: 'POST',
      });
      const data = await response.json();
      const presignedUrl = data.url;

      let contentType = `application/${fileType}`;

      if (['pdf', 'docx', 'doc', 'txt'].includes(fileType)) {
        if (fileType === 'txt') {
          contentType = 'text/plain';
        } else if (fileType === 'docx') {
          contentType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        } else if (fileType === 'doc') {
          contentType = 'application/msword';
        }
      }

      await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': contentType,
        },
      });

      setStatus('polling');
      const filename = data.id.split('.')[0];
      startPolling(filename);
    } catch (error) {
      console.error(error);
      setFailReason('network');
      setStatus('fail');
    }
  }, [file, fileType, startPolling, stopPolling]);

  const handleFileRemove = useCallback(() => {
    stopPolling();
    setFailReason(null);
    setFile(null);
    setFileType('');
    setTextUploaded(false);
    setStatus('initial');
  }, [stopPolling]);

  const reset = useCallback(() => {
    stopPolling();
    setFile(null);
    setStatus('initial');
    setFailReason(null);
    setFileType('');
    setTextInput('');
    setTextUploaded(false);
    setReport(null);
    setStyledReport(null);
    setDisclaimer(null);
    setContainsNDA(false);
    setNdaReport(null);
  }, [stopPolling]);

  // Cancel any active polling/timeout when the component unmounts.
  useEffect(() => stopPolling, [stopPolling]);

  return {
    file,
    fileType,
    status,
    failReason,
    textInput,
    textUploaded,
    report,
    styledReport,
    disclaimer,
    containsNDA,
    ndaReport,
    handleFileChange,
    handleFileRemove,
    handleTextChange,
    handleTextSubmit,
    handleUpload,
    reset,
  };
}
